import {
  Heart,
  Dumbbell,
  Apple,
  Moon,
  Snowflake,
  Pill,
  Wind,
  Brain,
  Sparkles,
  Sofa,
  Monitor,
  Wallet,
  Wrench,
  Package,
  Recycle,
  ShoppingBag,
} from 'lucide-react';
import type React from 'react';

/**
 * Disciplines — the recursive "what you're made of" tree (DS0).
 *
 * `User → Domains → [Areas — deferred] → Disciplines → Parameters → Body parts`.
 *
 * A **Discipline** is the levelable/unlockable node that sits under a domain
 * (the Fitness / Health node). Actions attach DIRECTLY to a discipline — the
 * "Workout" prime-habit bridge is collapsed; the discipline IS the aggregator.
 *
 * This file is the static registry (config = constants). Live level/XP roll-up
 * comes from each discipline's own store (Fitness rides `fitnessStore`); see
 * `utils/disciplineNodes.ts` + `hooks/useDisciplines.ts`. The unlock mechanic
 * (lock → unlock-by-domain-level + celebration) is wired in DS3 against the
 * existing `unlock_configurations` system — `unlockLevel`/`unlockKey` below are
 * the AI-fillable, DS3-ready metadata for it.
 *
 * Source of truth: `docs/DISCIPLINES_SPEC.md`.
 */

/** Alternate, toggleable renderers a discipline node can offer for its data. */
export type DisciplineVisual = 'radar' | 'grid' | 'list' | 'body-map' | 'bottle';

/** What a discipline's children are. The tree is ragged — branches differ. */
export type DisciplineChildType = 'parameters' | 'trackers' | 'leaf';

/**
 * Build maturity of a discipline's interior dashboard:
 * - `live`        — a real, working mini-dashboard (Fitness).
 * - `placeholder` — a "Coming Soon" dashboard exists as a sub-tab.
 * - `coming-soon` — no dashboard yet; appears in the overview only.
 */
export type DisciplineStatus = 'live' | 'placeholder' | 'coming-soon';

export interface DisciplineDefinition {
  /** Unique within its domain, e.g. 'fitness'. */
  id: string;
  /** Owning domain id, e.g. 'personal'. */
  domainId: string;
  name: string;
  /**
   * Optional compact label for space-tight surfaces (the Areas rail list + the
   * radar vertices), mirroring the Intellectual paths' `shortName`. Falls back to
   * `name` everywhere it isn't set — only the multi-word Ecological areas need it.
   */
  shortName?: string;
  description: string;
  icon: React.ElementType;
  /** Accent color for cards / radar vertices. Falls back to the domain color. */
  accent: string;
  status: DisciplineStatus;
  /** Whether a bespoke dashboard exists yet — drives its own sub-tab. */
  hasDashboard: boolean;
  /** Toggleable renderers this node declares (built with the feature). */
  visuals: DisciplineVisual[];
  /** What this node's children are (informational until the interiors land). */
  childType: DisciplineChildType;
  /**
   * Domain (total) level at which this discipline unlocks (DS3). `0` = always
   * available. Drives the locked/unlocked display (`useDisciplines`) and the
   * level-up → celebration grant (`useLevelUpDetection`). This is a test ladder
   * — tweak freely; the unlock_configurations seed should mirror these numbers.
   */
  unlockLevel: number;
  /** Reuses the global unlock system (unlock_configurations + UnlockCelebration). */
  unlockKey?: string;
}

/**
 * Personal domain — the 7 Disciplines (spec §4). Fitness is the framework
 * proof (live); the rest are built one-by-one in DS6+. Health / Nutrition &
 * Hydration / Supplements already have placeholder dashboards; Sleep /
 * Recovery / Breathing are overview-only teasers until their interiors land
 * (Breathing will adopt Breathwork from Reflection, keeping a doorway there).
 */
export const PERSONAL_DISCIPLINES: DisciplineDefinition[] = [
  {
    id: 'health',
    domainId: 'personal',
    name: 'Health',
    description: 'Vitals, symptoms, and overall wellbeing.',
    icon: Heart,
    accent: '#ef4444',
    status: 'live',
    hasDashboard: true,
    visuals: ['grid'],
    childType: 'trackers',
    unlockLevel: 3,
    unlockKey: 'discipline_personal_health',
  },
  {
    id: 'fitness',
    domainId: 'personal',
    name: 'Fitness',
    description: 'Performance parameters and muscle groups.',
    icon: Dumbbell,
    accent: '#f97316',
    status: 'live',
    hasDashboard: true,
    // Body-map joins the toggle in DS4; radar/grid/list ship today.
    visuals: ['radar', 'grid', 'list'],
    childType: 'parameters',
    unlockLevel: 5,
    unlockKey: 'discipline_personal_fitness',
  },
  {
    id: 'nourishment',
    domainId: 'personal',
    name: 'Nourishment',
    description: 'Food, water, and supplements — everything you take in.',
    icon: Apple,
    accent: '#10b981',
    // Merged area: hydration + food logging + supplement adherence all feed one
    // rank (see useDisciplines). Its dashboard stacks the three sections.
    status: 'live',
    hasDashboard: true,
    visuals: ['bottle', 'grid'],
    childType: 'trackers',
    unlockLevel: 1,
    unlockKey: 'discipline_personal_nutrition',
  },
  {
    id: 'rest',
    domainId: 'personal',
    name: 'Rest',
    description: 'Sleep and recovery — how you restore.',
    icon: Moon,
    accent: '#6366f1',
    // Merged area: the sleep log + recovery protocols feed one rank (see
    // useDisciplines). Its dashboard switches between the two workspaces.
    status: 'live',
    hasDashboard: true,
    visuals: ['grid'],
    childType: 'trackers',
    unlockLevel: 8,
    unlockKey: 'discipline_personal_sleep',
  },
  {
    id: 'breathing',
    domainId: 'personal',
    name: 'Coherence',
    description: 'Breath coherence training and breathwork sessions.',
    icon: Wind,
    accent: '#0ea5e9',
    // First area to adopt the 10-Rank ladder (its leaves = the 7 breath styles).
    status: 'live',
    hasDashboard: true,
    visuals: ['grid', 'list'],
    childType: 'parameters',
    unlockLevel: 18,
    unlockKey: 'discipline_personal_breathing',
  },
  {
    id: 'mind',
    domainId: 'personal',
    name: 'Mind',
    // Scope 006 (2026-09-30): Mind is becoming care practices. The id, unlock and
    // tracker kind are unchanged; only what the area is FOR is.
    description: 'Mental & emotional care.',
    icon: Brain,
    accent: '#8b5cf6',
    status: 'live',
    hasDashboard: true,
    visuals: ['grid'],
    childType: 'trackers',
    unlockLevel: 10,
    unlockKey: 'discipline_personal_mind',
  },
  {
    // Took the retired `emotions` slot. The mood/energy check-in that used to live
    // here moved to **Mind** (its data always lived in healthStore either way), so
    // nothing was lost — its XP re-attributes to Mind rather than evaporating.
    // ⚠️ The unlockKey stays `…_emotions` on purpose: it preserves the unlock users
    // already earned, the same legacy-key convention `nourishment`
    // (…_nutrition) and `rest` (…_sleep) follow. Migration 0209 relabels the row.
    id: 'grooming',
    domainId: 'personal',
    name: 'Grooming',
    description: 'Cleanliness, appearance, scent and presentation — your daily care system.',
    icon: Sparkles,
    accent: '#14b8a6',
    status: 'live',
    hasDashboard: true,
    visuals: ['radar', 'grid', 'list'],
    childType: 'parameters',
    unlockLevel: 4,
    unlockKey: 'discipline_personal_emotions',
  },
];

/**
 * Ecological domain — the 7 Areas (ECOLOGICAL_DOMAIN_SPEC §3). Unlike Personal's
 * disciplines these are NOT a "class" taxonomy — they're the facets of your life
 * infrastructure. Their rank is a derived view of the economy XP from the
 * habits / projects / finance / templates you tag to them (the "full reuse"
 * model — no parallel ledger). `physical_space`/`financial_systems` keep their
 * legacy snake_case ids for data continuity but display the renamed labels
 * (Environment Design / Financial Stewardship). All available (unlockLevel 0);
 * the domain's own 11-rank ladder (Unsettled→Ecosystem Architect) lives in
 * `constants/ecologicalRank.ts`. hasDashboard is true only where a bespoke
 * interior lands (Finance EC3, Possessions EC4, Resource Stewardship EC5).
 */
export const ECOLOGICAL_DISCIPLINES: DisciplineDefinition[] = [
  {
    id: 'physical_space',
    domainId: 'ecological',
    name: 'Environment Design',
    shortName: 'Environment',
    description: 'Cleaning rhythm, decluttering, zoning, comfort, recovery & focus spaces.',
    icon: Sofa,
    accent: '#10b981',
    status: 'live',
    hasDashboard: false,
    visuals: [],
    childType: 'leaf',
    unlockLevel: 0,
  },
  {
    id: 'digital_environment',
    domainId: 'ecological',
    name: 'Digital Environment',
    shortName: 'Digital',
    description: 'Inbox, files, and media — a clean, tidy digital life.',
    icon: Monitor,
    accent: '#3b82f6',
    status: 'live',
    hasDashboard: false,
    visuals: [],
    childType: 'leaf',
    unlockLevel: 0,
  },
  {
    id: 'financial_systems',
    domainId: 'ecological',
    name: 'Financial Stewardship',
    shortName: 'Finance',
    description: 'Personal finance tracking and budgeting.',
    icon: Wallet,
    accent: '#f59e0b',
    status: 'live',
    hasDashboard: false,
    visuals: ['list'],
    childType: 'trackers',
    unlockLevel: 0,
  },
  {
    id: 'household_operations',
    domainId: 'ecological',
    name: 'Household Operations',
    shortName: 'Household',
    description: 'Cleaning, laundry, dishes, meal prep, home maintenance, restocking.',
    icon: Wrench,
    accent: '#ef4444',
    status: 'live',
    hasDashboard: false,
    visuals: [],
    childType: 'leaf',
    unlockLevel: 0,
  },
  {
    id: 'possessions',
    domainId: 'ecological',
    name: 'Possessions',
    description: 'Cars, machines, clothes, gadgets — tracked and maintained.',
    icon: Package,
    accent: '#ec4899',
    status: 'live',
    hasDashboard: false,
    visuals: ['list'],
    childType: 'trackers',
    unlockLevel: 0,
  },
  {
    id: 'resource_stewardship',
    domainId: 'ecological',
    name: 'Resource Stewardship',
    shortName: 'Resources',
    description: 'Provisioning, recycling, and household staff & their scheduled tasks.',
    icon: Recycle,
    accent: '#14b8a6',
    status: 'live',
    hasDashboard: false,
    visuals: ['list'],
    childType: 'trackers',
    unlockLevel: 0,
  },
  {
    id: 'time_architecture',
    domainId: 'ecological',
    name: 'Acquisitions',
    shortName: 'Acquire',
    description: "Things you're planning to get — captured to offload the mental load, then acquired.",
    icon: ShoppingBag,
    accent: '#8b5cf6',
    status: 'live',
    hasDashboard: false,
    visuals: [],
    childType: 'leaf',
    unlockLevel: 0,
  },
];

/**
 * Registry of disciplines per domain. Personal proved the framework; Ecological
 * is the first non-Personal domain to adopt it (its 7 Areas).
 */
export const DISCIPLINES_BY_DOMAIN: Record<string, DisciplineDefinition[]> = {
  personal: PERSONAL_DISCIPLINES,
  ecological: ECOLOGICAL_DISCIPLINES,
};

/** All disciplines defined for a domain (empty if the domain has none yet). */
export function getDisciplines(domainId: string): DisciplineDefinition[] {
  return DISCIPLINES_BY_DOMAIN[domainId] ?? [];
}

/** A single discipline definition by domain + id. */
export function getDiscipline(
  domainId: string,
  disciplineId: string
): DisciplineDefinition | undefined {
  return getDisciplines(domainId).find(d => d.id === disciplineId);
}

/** Whether a domain has any disciplines wired up. */
export function domainHasDisciplines(domainId: string): boolean {
  return getDisciplines(domainId).length > 0;
}

/**
 * Disciplines that cross their unlock threshold when a domain levels from
 * `fromLevel` → `toLevel` (DS3). Used by the level-up detector to fire a
 * discipline-unlock celebration only on a *genuine* crossing — so already-met
 * thresholds never re-celebrate (the (from, to] half-open interval is the key).
 */
export function getDisciplinesUnlockedBetween(
  domainId: string,
  fromLevel: number,
  toLevel: number
): DisciplineDefinition[] {
  if (toLevel <= fromLevel) return [];
  return getDisciplines(domainId).filter(
    d => d.unlockLevel > fromLevel && d.unlockLevel <= toLevel
  );
}
